import type { FC } from 'react';
import { lazy, useMemo } from 'react';
import type { NetworkPointsSearchParams } from 'src/apis/monolith/types';
import { useEnabledSettings } from 'src/apis/monolith/use-enabled-settings';
import { useGeneralSettings } from 'src/apis/monolith/use-general-settings';
import { useNetworkPoints } from 'src/apis/monolith/use-network-points';
import { useSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { useOptionalTransportAuctionServiceRoot } from 'src/apis/transport-auction/use-transport-auction-service-root';
import { useTsoInterfaceSettings } from 'src/apis/transport-auction/use-tso-interface-settings';
import {
  PageSpinner,
  SpinnerContainer,
} from 'src/components/spinner-container';
import { useAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { Forbidden } from 'src/utils/errors';

const Page = lazy(
  () => import('src/pages/settings/general-transport-settings/page')
);

export const GeneralTransportSettings: FC = () => {
  const monolithUser = useAuthenticatedMonolithUser();
  if (!monolithUser.isTso) throw new Forbidden();

  const generalSettings = useGeneralSettings(monolithUser.referenceId);
  const enabledSettings = useEnabledSettings(monolithUser.referenceId);

  const networkPointsParams: NetworkPointsSearchParams = useMemo(
    () => ({
      bundled: false,
      specificTermsEnabled: true,
      tsoRefId: monolithUser.referenceId,
    }),
    [monolithUser.referenceId]
  );
  const networkPoints = useNetworkPoints(networkPointsParams);

  const surrenderRoot = useSurrenderRoot();

  const transportAuctionServiceRoot = useOptionalTransportAuctionServiceRoot();
  const tsoInterfaceSettings = useTsoInterfaceSettings({
    transportAuctionService: transportAuctionServiceRoot,
  });

  if (
    !generalSettings.response ||
    !enabledSettings.response ||
    !networkPoints.response ||
    !surrenderRoot.response ||
    transportAuctionServiceRoot?.loaded === false ||
    tsoInterfaceSettings?.response === null
  )
    return <PageSpinner />;

  return (
    <SpinnerContainer pending={generalSettings.pending}>
      <Page
        monolithUser={monolithUser}
        generalSettings={generalSettings}
        enabledSettings={enabledSettings}
        networkPoints={networkPoints}
        isSurrenderForAllEnabled={
          !!surrenderRoot.response.data._links.isSurrenderForAllEnabled
        }
        tsoInterfaceSettings={tsoInterfaceSettings}
      />
    </SpinnerContainer>
  );
};
